import type { CoreModuleOptions } from '../types'

/**
 * Cookiebot loaded from the SSR head instead of the GTM tag.
 *
 * Does what the "Cookiebot CMP" GTM template does, in the same order: consent
 * defaults, an instant update for returning visitors from the CookieConsent
 * cookie, ads data redaction, then uc.js. Keeping `implementation=gtm` in the
 * URL means uc.js behaves exactly as before, including its dataLayer events.
 *
 * @see https://developers.google.com/tag-platform/security/guides/consent
 * @see https://www.cookiebot.com/en/google-tag-manager/
 */
type ConsentOptions = CoreModuleOptions['consent']
type CookiebotOptions = NonNullable<NonNullable<ConsentOptions>['cookiebot']>

/** `consent.cookiebot` once it is known to load from the head, so `serial` is set. */
export type CookiebotHeadOptions = CookiebotOptions & { serial: string }

/** Consent Mode before the CMP has spoken, shared by the head bootstrap and `useGtag`. */
export const CONSENT_MODE_DENIED = {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
} as const

/** How long tags wait for Cookiebot's consent update before using the defaults, in ms. Same as the GTM template. */
const WAIT_FOR_UPDATE = 2000

/**
 * The Cookiebot options when the banner should come from the SSR head: provider is Cookiebot,
 * `loadFrom` is `'head'` and a serial is set. `undefined` means the GTM tag is still in charge,
 * so `useGtag` must keep setting the Consent Mode defaults itself.
 */
export function cookiebotHeadOptions(consent: ConsentOptions): CookiebotHeadOptions | undefined {
    const cookiebot = consent?.cookiebot

    if ((consent?.provider ?? 'cookiebot') !== 'cookiebot' || cookiebot?.loadFrom !== 'head' || !cookiebot.serial) return undefined

    return { ...cookiebot, serial: cookiebot.serial }
}

/** The uc.js URL as the GTM template requests it, so uc.js keeps its GTM behaviour and dataLayer events. */
export function cookiebotScriptUrl({ serial, cdnRegion = 'eu' }: CookiebotHeadOptions): string {
    return `https://consent.cookiebot.${cdnRegion}/uc.js?cbid=${encodeURIComponent(serial)}&implementation=gtm&consentmode-dataredaction=dynamic`
}

/**
 * The 90 kB script uc.js fetches next, which builds the banner. Its URL is predictable, so
 * it can be preloaded. `referer` must match `location.hostname` (no port) or the preload goes
 * unused; `dnt` mirrors the browser's Do Not Track flag, which the server cannot know, so a
 * DNT visitor's preload goes unused too (harmless).
 */
export function cookiebotConfigScriptUrl({ serial, cdnRegion = 'eu' }: CookiebotHeadOptions, hostname: string): string {
    return `https://consent.cookiebot.${cdnRegion}/${encodeURIComponent(serial)}/cc.js?renew=false&referer=${encodeURIComponent(hostname)}&dnt=false&init=false`
}

/**
 * The inline script: a `cookiebot_load_from: "head"` dataLayer flag (for a blocking trigger on the
 * GTM Cookiebot tag), gtag stub, defaults, returning-visitor update, redaction.
 */
export function cookiebotConsentBootstrap(): string {
    const defaults = {
        ...CONSENT_MODE_DENIED,
        functionality_storage: 'denied',
        personalization_storage: 'denied',
        security_storage: 'granted',
        wait_for_update: WAIT_FOR_UPDATE,
    }

    return [
        'window.dataLayer=window.dataLayer||[];',
        'dataLayer.push({cookiebot_load_from:"head"});',
        'function gtag(){dataLayer.push(arguments)}',
        'gtag("set",{url_passthrough:false,"developer_id.dMWZhNz":true});',
        `gtag("consent","default",${JSON.stringify(defaults)});`,
        'var m=document.cookie.match(/(?:^|; )CookieConsent=([^;]*)/),c=m?decodeURIComponent(m[1]):"";',
        'if(c.indexOf("{")===0){',
        'var o={};c.replace(/[{}]/g,"").split(",").forEach(function(p){var kv=p.split(":");o[kv[0]]=kv[1]==="true"});',
        'var g=function(v){return v?"granted":"denied"};',
        'gtag("consent","update",{ad_storage:g(o.marketing),ad_user_data:g(o.marketing),ad_personalization:g(o.marketing),analytics_storage:g(o.statistics),functionality_storage:g(o.preferences),personalization_storage:g(o.preferences),security_storage:"granted"});',
        'gtag("set",{ads_data_redaction:!o.marketing})',
        '}else{gtag("set",{ads_data_redaction:true})}',
    ].join('')
}
